import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { gql } from 'src/gql';
import { DEFAULT_CLIENTS_QUERY_LIMIT } from 'src/graphql';
import { useMembershipEmail } from 'src/graphql/entities/membership-email';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';

const SendMembershipEmailClientsQuery = gql(`
  query SendMembershipEmailClientsQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        clientId
        firstName
        lastName
        fullName @client
        primaryEmail {
          id
          value
        }
        emails {
          id
          value
        }
      }
    }
  }
`);

const ClientCardContainer = styled(Card, {
  name: 'ClientCardContainer',
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  background: isSelected ? theme.palette.custom.OV.sys.surfaceVariant : 'none',
  border: '1px solid',
  borderColor: isSelected
    ? theme.palette.custom.OV.sys.secondary
    : theme.palette.custom.OV.sys.outlineVariant,
  cursor: 'pointer',
  '&:hover': {
    background: theme.palette.custom.OV.sys.surfaceVariant,
  },
}));

const ClientCard = ({
  name,
  email,
  isSelected,
  onClick,
}: {
  name: string;
  email: string;
  isSelected: boolean;
  onClick: () => void;
}) => {
  if (!name || !email) {
    return null;
  }

  return (
    <ClientCardContainer isSelected={isSelected} onClick={onClick}>
      <DynamicIcon mr={1} name="person" />
      <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" component="div" display="flex" alignItems="center">
            {name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      </CardContent>
    </ClientCardContainer>
  );
};

export const SendMembershipEmailModal = () => {
  const theme = useTheme();
  const { closeModal } = useModalMethods(Modals.SendMembershipEmail);
  const { isOpened, modalProps } = useModalState(Modals.SendMembershipEmail);

  const [selectedClient, setSelectedClient] = useState('');

  const filters = useMemo(
    () =>
      modalProps?.address?.addressId
        ? [
            {
              attribute: 'addresses/addressId',
              operator: '=',
              value: modalProps?.address?.addressId || '',
            },
          ]
        : [],
    [modalProps?.address?.addressId],
  );

  const { data } = useQuery(SendMembershipEmailClientsQuery, {
    variables: {
      params: {
        filters,
        limit: DEFAULT_CLIENTS_QUERY_LIMIT,
      },
    },
    skip: !filters.length,
  });

  const clients = useMemo(
    () =>
      (data?.getClient?.records ?? []).filter(
        (client): client is NonNullable<typeof client> => client !== null,
      ),
    [data],
  );

  const { sendMembershipEmail, loading: isSubmittingMembershipEmail } = useMembershipEmail();

  const { showSuccessAlert } = useNotifications();

  const onSubmit = useCallback(async () => {
    await sendMembershipEmail({
      addressId: modalProps?.address?.addressId || '',
      clientId: selectedClient,
    });

    showSuccessAlert('Email sent!');
    setSelectedClient('');
    closeModal();
  }, [
    closeModal,
    modalProps?.address?.addressId,
    selectedClient,
    sendMembershipEmail,
    showSuccessAlert,
  ]);

  const onClose = useCallback(() => {
    setSelectedClient('');
    closeModal();
  }, [closeModal]);

  return (
    <Dialog open={isOpened} onClose={onClose} fullWidth>
      <DialogTitle>
        <Typography>Send Membership Email</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Select a client:
        </Typography>
        {(clients ?? [])
          .sort((c) => (c.clientId === modalProps?.address?.primaryClientId ? -1 : 1))
          .map((client) => (
            <ClientCard
              key={client.clientId}
              name={client.fullName}
              email={client.primaryEmail?.value || client.emails?.[0]?.value || ''}
              isSelected={selectedClient === client.clientId}
              onClick={() => setSelectedClient(client.clientId)}
            />
          ))}
        {!clients.length && <Typography>No clients found</Typography>}
        <Button
          sx={{ float: 'right', marginTop: theme.spacing(2) }}
          onClick={onSubmit}
          disabled={!selectedClient || isSubmittingMembershipEmail}
        >
          Submit
        </Button>
      </DialogContent>
    </Dialog>
  );
};
